import { nextTick, ref } from 'vue';
import { useAPI } from '@/composables/useAPI';
import { userLoginFailedNotification } from '@/core/notifications';
import {
  renderTurnstileWidget,
  resetTurnstileWidget,
  turnstileSiteKey,
} from '@/core/turnstile';
import { useUserStore } from '@/stores/user';
import { emitter } from '@/core/emitter';
import { PostHogUtils } from '@/core/PostHogUtils';

// The login exchange itself — credentials, the Turnstile challenge retry, the
// analytics events and the post-login KYC handoff — shared by the login PAGE
// and the login POPUP. The only thing that differs between the two is what
// happens once the user is logged in (the page navigates home, the popup just
// closes and leaves the user where they were), so that is the one piece the
// caller supplies as `onSuccess`.
export function useLoginForm({ onSuccess } = {}) {
  const { request } = useAPI('/user/login');
  const userStore = useUserStore();

  const loading = ref(false);
  const username = ref('');
  const password = ref('');

  // The "prove you're human" flow. While the platform is under attack, login
  // answers 428 with details.code CHALLENGE_REQUIRED; we render a Cloudflare
  // Turnstile widget (usually invisible, ~a second) and resubmit the same
  // credentials with its single-use token as challengeToken.
  const challengeRequired = ref(false);
  const challengeWidgetEl = ref(null);
  const challengeWidgetId = ref(null);
  const challengeToken = ref('');

  function hasUnsafePasswordChecks(checks) {
    if (!checks || typeof checks !== 'object') return false;
    return Object.values(checks).some((v) => v === true);
  }

  // Collapse the server's login error into a bounded set of reasons. The raw
  // message never goes on the event — it can echo back what was typed — and the
  // entered username/email is never attached either.
  function loginFailureReason(error) {
    const message = String(error?.message || error || '');
    if (/invalid|incorrect|credential|password/i.test(message)) {
      return 'bad_credentials';
    }
    if (/locked|disabled|suspend/i.test(message)) return 'account_locked';
    if (/network/i.test(message)) return 'network';
    return 'error';
  }

  function isChallengeRequired(error) {
    return error?.details?.code === 'CHALLENGE_REQUIRED';
  }

  async function showChallenge() {
    if (!turnstileSiteKey()) {
      // No widget configured for this build — fall back to the same message a
      // rate limit would have produced instead of a dead end.
      userLoginFailedNotification('Too many attempts. Please try again later.');
      return;
    }
    if (challengeRequired.value && challengeWidgetEl.value?.hasChildNodes()) {
      // Already on screen; the token we sent was spent or expired — ask
      // Turnstile for a fresh one.
      resetTurnstileWidget(challengeWidgetId.value);
      return;
    }
    // Either the first challenge, or the loading spinner's v-if recycled the
    // container mid-retry and the widget must be rendered into the fresh node.
    challengeRequired.value = true;
    await nextTick();
    try {
      challengeWidgetId.value = await renderTurnstileWidget(
        challengeWidgetEl.value,
        (token) => {
          challengeToken.value = token;
          logIn();
        },
      );
    } catch {
      challengeRequired.value = false;
      userLoginFailedNotification('Too many attempts. Please try again later.');
    }
  }

  async function logIn() {
    try {
      loading.value = true;
      const loginRequest = {
        username: username.value,
        password: password.value,
      };
      if (challengeToken.value) {
        loginRequest.challengeToken = challengeToken.value;
        challengeToken.value = '';
      }
      const response = await request(loginRequest);
      const securityChecks = response.user?.passwordSecurityChecks;
      if (hasUnsafePasswordChecks(securityChecks)) {
        userStore.setPasswordHighlighted(true);
        emitter.emit('showAlert', {
          shown: true,
          type: 'password-unsafe',
          status: 'warning',
          title: 'Warning: Unsafe Password',
          detail: 'Update your password to stay secure.',
        });
      }
      userStore.setLoggedIn(response.user);
      // Must come after setLoggedIn: that call runs syncAnalyticsIdentity() ->
      // identify(), which is what opts the device back in. capture() is a no-op
      // until then, so a login_succeeded fired any earlier would be dropped.
      // setLoggedIn has no await before identify, so it has already run here.
      PostHogUtils.capture('login_succeeded');
      const veriffData = response.user?.veriffData;
      await onSuccess?.(response);

      if (veriffData?.required !== true || veriffData?.status === 'approved')
        return;

      PostHogUtils.markKycRequired();

      if (!veriffData?.sessionID) {
        emitter.emit('showVeriffModal');
        return;
      }

      await userStore.verifyUserKYC(veriffData.sessionID);
      if (userStore.user?.veriffData?.status !== 'approved') {
        emitter.emit('showVeriffModal');
      }
    } catch (error) {
      if (isChallengeRequired(error)) {
        loading.value = false;
        await showChallenge();
        return;
      }
      // A failed login never identifies, so this no-ops on a device that has
      // never opted in (first-ever login on a fresh browser). That's expected —
      // the failure breakdown is only measurable for returning devices, and we
      // deliberately don't opt anonymous visitors in just to record a bad login.
      PostHogUtils.capture('login_failed', {
        reason: loginFailureReason(error),
      });
      userLoginFailedNotification(error);
      loading.value = false;
    }
  }

  return {
    loading,
    username,
    password,
    challengeRequired,
    challengeWidgetEl,
    logIn,
  };
}
